import { Fragment, useState, useEffect, useCallback } from 'react'
import { auditApi, type ActivityEntry, type AuditActivityFilters, type AuditEntryDetail, type LoginEntry } from '../api/audit'
import {
  Page,
  PageHeader,
  FilterBar,
  Tabs,
  Button,
  Callout,
  LoadingState,
  CircularProgress,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Icon,
  TextField,
  Select,
  Pagination,
  List,
  ListItem,
} from '../components/ui'

// TASK-0093: `eventType` is now the literal dotted action name the audit ledger records (e.g.
// "login.succeeded", "sessions.revoked", "customer.merged"), not the old fixed 4-value vocabulary
// ("login", "lead_created", ...) a synthesized feed used to emit. Keying off the first dot-segment
// keeps a real icon/color for every action family the ledger already writes, and — same reasoning
// as the backend's generic description fallback — degrades to the existing 'info'/'text-muted'
// default for any action family not listed here, rather than requiring this map to be extended
// every time a new module starts writing to the ledger.
const EVENT_ICONS: Record<string, string> = {
  login: 'login',
  account: 'lock',
  sessions: 'logout',
  contact: 'merge_type',
  person: 'merge_type',
  company: 'merge_type',
  customer: 'merge_type',
}

const EVENT_COLORS: Record<string, string> = {
  login: 'text-primary',
  account: 'text-danger',
  sessions: 'text-warning',
  contact: 'text-info',
  person: 'text-info',
  company: 'text-info',
  customer: 'text-info',
}

function eventPrefix(eventType: string): string {
  return eventType.split('.')[0]
}

function formatDateTime(s: string): string {
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function timeAgo(s: string): string {
  const diff = (Date.now() - new Date(s).getTime()) / 1000
  if (diff < 60) return 'ora'
  if (diff < 3600) return `${Math.floor(diff / 60)}m fa`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`
  return `${Math.floor(diff / 86400)}g fa`
}

type Tab = 'activity' | 'logins' | 'locked'

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

/** F-109: tabella campo / prima / dopo di una riga del ledger; gli importi nascosti sono dichiarati. */
function ChangeDiff({ detail }: { detail: AuditEntryDetail }) {
  const keys = Array.from(new Set([...Object.keys(detail.before ?? {}), ...Object.keys(detail.after ?? {})]))
  return (
    <div>
      {detail.motivation && <div className="md-typescale-body-small mb-1"><span className="text-muted">Motivo: </span>{detail.motivation}</div>}
      {keys.length === 0 ? (
        <div className="text-muted md-typescale-body-small">Nessun dettaglio prima/dopo registrato.</div>
      ) : (
        <DataTable>
          <thead><tr><th>Campo</th><th>Prima</th><th>Dopo</th></tr></thead>
          <tbody>
            {keys.map(k => (
              <tr key={k}>
                <td className="text-muted">{k}</td>
                <td>{formatValue(detail.before?.[k])}</td>
                <td>{formatValue(detail.after?.[k])}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {detail.amountsHidden && (
        <div className="text-muted md-typescale-body-small">Gli importi non sono mostrati: serve il permesso di vedere gli importi reali.</div>
      )}
    </div>
  )
}

export function AuditPage() {
  const [tab, setTab] = useState<Tab>('activity')

  const [activities, setActivities] = useState<ActivityEntry[]>([])
  const [actLoading, setActLoading] = useState(false)
  const [actError, setActError] = useState('')

  // TASK-0099: "Attività recente" filter bar. Same pending/applied split LeadsPage.tsx uses for
  // its own filter form — inputs update the pending draft as the operator types, and only
  // "Applica"/"Cerca" (or "Reimposta") commits it to `actFilters`, which is what actually drives
  // the fetch below. This keeps every keystroke from re-querying the audit ledger.
  const emptyActFilters = { actorUserId: '', action: '', ipAddress: '', from: '', to: '', hasMotivation: '' }
  const [actFilterDraft, setActFilterDraft] = useState(emptyActFilters)
  const [actFilters, setActFilters] = useState(emptyActFilters)
  // Whether the currently-applied filters are all empty — distinguishes "no rows at all" from "no
  // rows matching your filters" for the empty-state message below.
  const actFiltersActive = Object.values(actFilters).some(v => v !== '')

  // F-109: dettaglio prima/dopo di una riga (aperto a richiesta).
  const [openId, setOpenId] = useState<number | null>(null)
  const [detail, setDetail] = useState<AuditEntryDetail | null>(null)
  const [detailError, setDetailError] = useState('')

  function toggleDetail(id: number | undefined): void {
    if (id == null) return
    if (openId === id) { setOpenId(null); setDetail(null); return }
    setOpenId(id); setDetail(null); setDetailError('')
    auditApi.entry(id)
      .then(setDetail)
      .catch(e => setDetailError(e instanceof Error ? e.message : 'Errore'))
  }

  const [loginItems, setLoginItems] = useState<LoginEntry[]>([])
  const [loginTotal, setLoginTotal] = useState(0)
  const [loginPage, setLoginPage] = useState(1)
  const [loginLoading, setLoginLoading] = useState(false)

  const [lockedItems, setLockedItems] = useState<Array<{ id: number; name: string; email: string; loginAttempts: number; lockedUntil: string }>>([])
  const [lockedLoading, setLockedLoading] = useState(false)

  // TASK-0099: builds the request from `actFilters` (the applied, not the draft, values) — a
  // blank field is simply omitted, so with every filter left empty this sends exactly the same
  // `limit=100`/no-filter request AuditPage always sent, per the task's "no filters → identical
  // behavior to today" requirement.
  const loadActivity = useCallback(() => {
    setActLoading(true); setActError('')
    const request: AuditActivityFilters = { limit: 100 }
    if (actFilters.actorUserId.trim()) {
      const n = Number(actFilters.actorUserId.trim())
      if (!Number.isNaN(n)) request.actorUserId = n
    }
    if (actFilters.action.trim()) request.action = actFilters.action.trim()
    if (actFilters.ipAddress.trim()) request.ipAddress = actFilters.ipAddress.trim()
    if (actFilters.from) request.from = actFilters.from
    if (actFilters.to) request.to = actFilters.to
    if (actFilters.hasMotivation === 'yes') request.hasMotivation = true
    if (actFilters.hasMotivation === 'no') request.hasMotivation = false
    auditApi.activity(request)
      .then(r => setActivities(r.items))
      .catch(e => setActError(e instanceof Error ? e.message : 'Errore'))
      .finally(() => setActLoading(false))
  }, [actFilters])

  useEffect(() => {
    if (tab === 'activity') loadActivity()
  }, [tab, loadActivity])

  function applyActFilters(): void {
    setActFilters(actFilterDraft)
  }

  function resetActFilters(): void {
    setActFilterDraft(emptyActFilters)
    setActFilters(emptyActFilters)
  }

  useEffect(() => {
    if (tab !== 'logins') return
    setLoginLoading(true)
    auditApi.logins(loginPage)
      .then(r => { setLoginItems(r.items); setLoginTotal(r.total) })
      .catch(() => {})
      .finally(() => setLoginLoading(false))
  }, [tab, loginPage])

  useEffect(() => {
    if (tab !== 'locked') return
    setLockedLoading(true)
    auditApi.lockedUsers()
      .then(r => setLockedItems(r.items))
      .catch(() => {})
      .finally(() => setLockedLoading(false))
  }, [tab])

  const loginPages = Math.max(1, Math.ceil(loginTotal / 25))

  return (
    <Page>
      <PageHeader
        title="Audit log"
        icon="history"
        tabs={
          <Tabs
            aria-label="Sezioni audit"
            items={[
              { id: 'activity', label: 'Attività recente', icon: 'timeline' },
              { id: 'logins', label: 'Accessi', icon: 'login' },
              { id: 'locked', label: 'Utenti bloccati', icon: 'lock' },
            ]}
            value={tab}
            onChange={id => setTab(id as Tab)}
          />
        }
      />

      {tab === 'activity' && (
        <>
          {/* TASK-0099: filter bar for GET /api/audit/activity's filter params (TASK-0096).
              entityType/entityId are deliberately not exposed here — see api/audit.ts's
              AuditActivityFilters doc comment. */}
          <FilterBar onSubmit={applyActFilters} onReset={resetActFilters}>
            <TextField
              label="Utente (ID)"
              type="number"
              supportingText="es. 42"
              value={actFilterDraft.actorUserId}
              onChange={e => setActFilterDraft(f => ({ ...f, actorUserId: e.target.value }))}
            />
            <TextField
              label="Azione (match esatto)"
              type="text"
              supportingText="es. login.succeeded"
              value={actFilterDraft.action}
              onChange={e => setActFilterDraft(f => ({ ...f, action: e.target.value }))}
            />
            <TextField
              label="Indirizzo IP"
              type="text"
              supportingText="es. 10.0.0.1"
              value={actFilterDraft.ipAddress}
              onChange={e => setActFilterDraft(f => ({ ...f, ipAddress: e.target.value }))}
            />
            <TextField
              label="Dal"
              type="date"
              value={actFilterDraft.from}
              onChange={e => setActFilterDraft(f => ({ ...f, from: e.target.value }))}
            />
            <TextField
              label="Al"
              type="date"
              value={actFilterDraft.to}
              onChange={e => setActFilterDraft(f => ({ ...f, to: e.target.value }))}
            />
            <Select
              label="Motivazione"
              value={actFilterDraft.hasMotivation}
              onChange={e => setActFilterDraft(f => ({ ...f, hasMotivation: e.target.value }))}
            >
              <option value="">Tutte</option>
              <option value="yes">Con motivazione</option>
              <option value="no">Senza motivazione</option>
            </Select>
          </FilterBar>

          <Card variant="outlined" padding="none">
            {actLoading ? (
              <LoadingState />
            ) : actError ? (
              <div className="p-3"><Callout tone="error">{actError}</Callout></div>
            ) : activities.length === 0 ? (
              <EmptyState
                icon="timeline"
                title={actFiltersActive ? 'Nessuna attività trovata con questi filtri' : 'Nessuna attività registrata'}
              />
            ) : (
              <List aria-label="Attività registrate">
                {activities.map((a, i) => (
                  <Fragment key={a.id ?? i}>
                    <ListItem
                      leading={
                        <Icon
                          name={EVENT_ICONS[eventPrefix(a.eventType)] ?? 'info'}
                          size={24}
                          className={EVENT_COLORS[eventPrefix(a.eventType)] ?? 'text-muted'}
                        />
                      }
                      headline={a.description}
                      supporting={
                        <>
                          {formatDateTime(a.occurredAt)}
                          {a.motivation && (
                            <span className="d-block md-typescale-body-small mt-1" data-testid="audit-motivation">
                              <span className="text-muted">Motivo: </span>{a.motivation}
                            </span>
                          )}
                        </>
                      }
                      trailing={
                        <span className="d-flex align-items-center gap-3">
                          <span className="text-muted md-typescale-body-small text-nowrap">{timeAgo(a.occurredAt)}</span>
                          {a.id != null && (
                            <Button
                              variant="text"
                              size="sm"
                              className="text-nowrap"
                              onClick={() => toggleDetail(a.id)}
                            >
                              {openId === a.id ? 'Chiudi' : 'Prima/dopo'}
                            </Button>
                          )}
                        </span>
                      }
                    />
                    {openId === a.id && (
                      <li className="md-list__li px-3 pb-3">
                        {detailError ? (
                          <Callout tone="error">{detailError}</Callout>
                        ) : !detail ? (
                          <CircularProgress size="sm" />
                        ) : (
                          <ChangeDiff detail={detail} />
                        )}
                      </li>
                    )}
                  </Fragment>
                ))}
              </List>
            )}
          </Card>
        </>
      )}

      {tab === 'logins' && (
        <Card variant="outlined" padding="none">
          {loginLoading ? (
            <LoadingState />
          ) : (
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Utente</th>
                  <th>Email</th>
                  <th>Ruolo</th>
                  <th>Ultimo accesso</th>
                  <th>Tentativi falliti</th>
                </tr>
              </thead>
              <tbody>
                {loginItems.map(u => (
                  <tr key={u.id}>
                    <td className="md-typescale-emphasized-body-medium">{u.name}</td>
                    <td className="text-muted md-typescale-body-small">{u.email}</td>
                    <td><StatusPill tone="neutral">{u.role.replace('Equal', '')}</StatusPill></td>
                    <td className="text-muted md-typescale-body-small">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : '—'}</td>
                    <td className={`md-num ${u.loginAttempts > 0 ? 'text-warning md-typescale-emphasized-body-medium' : 'text-muted'}`}>{u.loginAttempts}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}
          {loginPages > 1 && <Pagination page={loginPage} pageSize={25} total={loginTotal} onChange={setLoginPage} />}
        </Card>
      )}

      {tab === 'locked' && (
        <Card variant="outlined" padding="none">
          {lockedLoading ? (
            <LoadingState />
          ) : lockedItems.length === 0 ? (
            <EmptyState icon="lock_open" title="Nessun utente bloccato" />
          ) : (
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Utente</th>
                  <th>Email</th>
                  <th>Tentativi</th>
                  <th>Bloccato fino</th>
                </tr>
              </thead>
              <tbody>
                {lockedItems.map(u => (
                  <tr key={u.id}>
                    <td className="md-typescale-emphasized-body-medium">{u.name}</td>
                    <td className="text-muted md-typescale-body-small">{u.email}</td>
                    <td className="text-danger md-num md-typescale-emphasized-body-medium">{u.loginAttempts}</td>
                    <td className="text-muted md-typescale-body-small">{formatDateTime(u.lockedUntil)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}
        </Card>
      )}
    </Page>
  )
}
